"use client";
import React from "react";
import { motion } from "framer-motion";
import Grid from "@mui/material/Grid";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  CheckCircle,
  School,
  ArrowForward,
  PlayArrow,
  Download,
  Phone,
  Email,
  CalendarMonth,
} from "@mui/icons-material";
import { SelectChangeEvent } from "@mui/material/Select";
import CountUp from "react-countup";

type UseInViewOptions = {
  threshold?: number | number[];
  root?: Element | null;
  rootMargin?: string;
  triggerOnce?: boolean;
};

// Custom hook for intersection observer
const useInView = (options: UseInViewOptions = {}) => {
  const { threshold = 0, root = null, rootMargin = "0px", triggerOnce = false } = options;
  const [ref, setRef] = React.useState<Element | null>(null);
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    if (!ref) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (triggerOnce) {
            observer.unobserve(entry.target);
          }
        } else {
          if (!triggerOnce) {
            setInView(false);
          }
        }
      },
      { threshold, root, rootMargin }
    );

    observer.observe(ref);

    return () => {
      try {
        observer.disconnect();
      } catch {
        /* ignore */
      }
    };
  }, [ref, threshold, root, rootMargin, triggerOnce]);

  return [setRef, inView] as const;
};

const CTASection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  const [program, setProgram] = React.useState("");
  
  const [formData, setFormData] = React.useState({
    name: "",
    email: "",
    phone: "",
    program: "",
  });

  // Use the custom hook for counters
  const [setCountersRef, countersInView] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  const colors = {
    primary: "#1a237e",
    secondary: "#d32f2f",
    accent: "#ff6f00",
    light: "#f5f5f5",
    dark: "#0d1b2a",
    text: "#333333",
    success: "#2e7d32",
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    console.log("Form submitted:", formData);
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const pulseAnimation = {
    scale: [1, 1.05, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
    }
  };

  const programs = [
    "B.Tech Computer Science",
    "B.Tech Mechanical Engineering",
    "B.Tech Civil Engineering",
    "B.Tech Electronics & Communication",
    "B.Tech Electrical Engineering",
    "M.Tech Programs",
    "MBA",
    "BBA",
  ];

  const benefits = [
    "Industry-Ready Curriculum",
    "100% Placement Assistance",
    "Modern Campus Facilities",
    "Expert Faculty",
    "Industry Partnerships",
    "Research Opportunities",
  ];

  const stats = [
    { number: 25000, suffix: "+", label: "Successful Alumni" },
    { number: 95, suffix: "%", label: "Placement Rate" },
    { number: 50, suffix: "+", label: "Industry Partners" },
    { number: 15, suffix: "+", label: "Years of Excellence" },
  ];

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${colors.dark} 0%, ${colors.primary} 100%)`,
        position: "relative",
        overflow: "hidden",
        py: { xs: 8, md: 12 },
      }}
    >
      {/* Background Elements */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, ${colors.accent}20 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, ${colors.secondary}15 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, ${colors.primary}25 0%, transparent 50%)
          `,
        }}
      />
      
      <Container maxWidth="lg">
        {/* Stats Counter Section */}
        <div ref={setCountersRef}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Grid container spacing={4} sx={{ mb: 8 }}>
              {stats.map((stat, index) => (
                <Grid key={stat.label} size={{ xs: 6, md: 3 }}>
                  <Box sx={{ textAlign: "center", color: "white" }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "2rem", md: "2.5rem", lg: "3rem" },
                        background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.secondary} 100%)`,
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        mb: 1,
                        fontFeatureSettings: '"tnum"',
                      }}
                    >
                      {countersInView ? (
                        <CountUp
                          start={0}
                          end={stat.number}
                          duration={2.5}
                          separator=","
                          suffix={stat.suffix}
                          delay={index * 0.2}
                        />
                      ) : (
                        `0${stat.suffix}`
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.9rem", md: "1rem" },
                        opacity: 0.9,
                        lineHeight: 1.3,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </div>

        <Grid container spacing={6} alignItems="center">
          {/* Left Content - Main CTA */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {/* Badge */}
              <motion.div variants={fadeInUp}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    bgcolor: colors.accent,
                    color: "white",
                    px: 3,
                    py: 1,
                    borderRadius: "20px",
                    mb: 3,
                  }}
                >
                  <School sx={{ fontSize: 20, mr: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Admissions Open 2024
                  </Typography>
                </Box>
              </motion.div>

              {/* Main Heading */}
              <motion.div variants={fadeInUp}>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    color: "white",
                    mb: 2,
                    fontSize: { xs: "2.5rem", md: "3.5rem" },
                    lineHeight: 1.2,
                  }}
                >
                  Start Your Engineering
                  <Box
                    component="span"
                    sx={{
                      background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.secondary} 100%)`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      display: "block",
                    }}
                  >
                    Journey Today
                  </Box>
                </Typography>
              </motion.div>

              {/* Subheading - Updated with counter reference */}
              <motion.div variants={fadeInUp}>
                <Typography
                  variant="h5"
                  sx={{
                    color: "rgba(255, 255, 255, 0.9)",
                    mb: 4,
                    fontWeight: 400,
                    fontSize: { xs: "1.2rem", md: "1.4rem" },
                    lineHeight: 1.6,
                  }}
                >
                  Join{" "}
                  <Box component="span" sx={{ fontWeight: 700, color: colors.accent }}>
                    {countersInView ? (
                      <CountUp start={0} end={25000} duration={3} separator="," suffix="+" />
                    ) : (
                      "25,000+"
                    )}
                  </Box>{" "}
                  successful alumni who started their careers at Shivalik College of Engineering. 
                  Your dream career begins here.
                </Typography>
              </motion.div>

              {/* Benefits List */}
              <motion.div variants={fadeInUp}>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  {benefits.map((benefit, index) => (
                    <Grid key={benefit} size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <CheckCircle
                          sx={{
                            color: colors.accent,
                            fontSize: 20,
                            mr: 2,
                          }}
                        />
                        <Typography
                          variant="body1"
                          sx={{
                            color: "white",
                            fontWeight: 500,
                          }}
                        >
                          {benefit}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div variants={fadeInUp}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems="center">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForward />}
                      sx={{
                        background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.secondary} 100%)`,
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        px: 5,
                        py: 1.8,
                        borderRadius: "30px",
                        boxShadow: `0 12px 30px ${colors.accent}40`,
                        whiteSpace: "nowrap",
                        minWidth: { xs: "100%", sm: "auto" },
                        "&:hover": {
                          background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.accent} 100%)`,
                          boxShadow: `0 16px 40px ${colors.accent}60`,
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Apply Now
                    </Button>
                  </motion.div>

                  <Stack direction="row" spacing={2}>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="outlined"
                        size="large"
                        startIcon={<PlayArrow />}
                        onClick={() => { window.open("https://virtualtour.shivalik.edu.in", "_blank"); }}
                        sx={{
                          borderColor: "white",
                          color: "white",
                          fontWeight: 600,
                          px: 3,
                          py: 1.8,
                          borderRadius: "30px",
                          borderWidth: 2,
                          "&:hover": {
                            borderColor: colors.accent,
                            color: colors.accent,
                            backgroundColor: "rgba(255, 111, 0, 0.1)",
                            transform: "translateY(-2px)",
                          },
                          transition: "all 0.3s ease",
                          
                          
                        }}
                      >
                        Virtual Tour
                      </Button>
                    </motion.div>

                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="text"
                        size="large"
                        onClick={() => { window.open("https://shivalikcollege.edu.in/prospectus/", "_blank"); }}
                        startIcon={<Download />}
                        sx={{
                          color: "white",
                          fontWeight: 600,
                          px: 3,
                          py: 1.8,
                          borderRadius: "30px",
                          "&:hover": {
                            color: colors.accent,
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                            transform: "translateY(-2px)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        Brochure
                      </Button>
                    </motion.div>
                  </Stack>
                </Stack>
              </motion.div>

              {/* Contact Info */}
              <motion.div variants={fadeInUp}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ mt: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", color: "white" }}>
                    <Phone sx={{ mr: 1, color: colors.accent }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      +91 9876543210
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", color: "white" }}>
                    <Email sx={{ mr: 1, color: colors.accent }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      admissions@shivalik.edu.in
                    </Typography>
                  </Box>
                </Stack>
              </motion.div>
            </motion.div>
          </Grid>

          {/* Right Content - Application Form */}
          <Grid size={{ xs: 12, lg: 5 }}>
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Card
                sx={{
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                  borderRadius: "24px",
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  {/* Form Header */}
                  <Box sx={{ textAlign: "center", mb: 4 }}>
                    <motion.div animate={pulseAnimation}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 2,
                        }}
                      >
                        <CalendarMonth sx={{ color: "white", fontSize: 30 }} />
                      </Box>
                    </motion.div>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: colors.dark,
                        mb: 1,
                      }}
                    >
                      Get Admission Info
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.text, opacity: 0.8 }}
                    >
                      Fill this form to get complete details about admissions
                    </Typography>
                  </Box>

                  {/* Application Form */}
                  <Box component="form" onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        variant="outlined"
                        value={formData.name}
                        onChange={handleInputChange("name")}
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            "&:hover fieldset": {
                              borderColor: colors.primary,
                            },
                          },
                        }}
                      />

                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        variant="outlined"
                        value={formData.email}
                        onChange={handleInputChange("email")}
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            "&:hover fieldset": {
                              borderColor: colors.primary,
                            },
                          },
                        }}
                      />

                      <TextField
                        fullWidth
                        label="Phone Number"
                        type="tel"
                        variant="outlined"
                        value={formData.phone}
                        onChange={handleInputChange("phone")}
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            "&:hover fieldset": {
                              borderColor: colors.primary,
                            },
                          },
                        }}
                      />

                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Program Interest</InputLabel>
                        <Select
                          value={formData.program}
                          onChange={handleInputChange("program")}
                          label="Program Interest"
                          sx={{
                            borderRadius: "12px",
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: colors.primary,
                            },
                          }}
                        >
                          {programs.map((program) => (
                            <MenuItem key={program} value={program}>
                              {program}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          size="large"
                          sx={{
                            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                            fontWeight: 700,
                            fontSize: "1.1rem",
                            py: 1.5,
                            borderRadius: "12px",
                            boxShadow: `0 8px 25px ${colors.primary}40`,
                            "&:hover": {
                              background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primary} 100%)`,
                              boxShadow: `0 12px 30px ${colors.primary}60`,
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          Get Admission Details
                        </Button>
                      </motion.div>

                      <Typography
                        variant="caption"
                        sx={{
                          textAlign: "center",
                          color: colors.text,
                          opacity: 0.7,
                          display: "block",
                        }}
                      >
                        * Our counselor will contact you within 24 hours
                      </Typography>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default CTASection;